import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";

interface HealthDataFormProps {
  memberId: string;
  latestHealthData: any;
  onDataSubmitted: () => void;
  onClose: () => void;
}

const fieldNames: { [key: string]: string } = {
  rhr: "Resting Heart Rate",
  systolic: "Systolic Blood Pressure",
  diastolic: "Diastolic Blood Pressure",
  bmi: "Body Mass Index",
  wc: "Waist Circumference",
  fpg: "Fasting Plasma Glucose",
  pt: "Plasma Triglyceride",
  tc: "Total Cholesterol",
  hdl: "HDL Cholesterol",
  ldl: "LDL Cholesterol",
};

const HealthDataForm: React.FC<HealthDataFormProps> = ({
  memberId,
  latestHealthData,
  onDataSubmitted,
  onClose,
}) => {
  const [formData, setFormData] = useState<{ [key: string]: string }>({});
  const [initialData, setInitialData] = useState<{ [key: string]: string }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [riskScore, setRiskScore] = useState<number | null>(null);
  const [missingParameters, setMissingParameters] = useState<string[]>([]);

  useEffect(() => {
    if (latestHealthData) {
      const updatedFormData: { [key: string]: string } = {};
      Object.keys(fieldNames).forEach((key) => {
        updatedFormData[key] = latestHealthData[key]?.toString() || "";
      });
      setFormData(updatedFormData);
      setInitialData(updatedFormData);
    }
  }, [latestHealthData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (value === "" && initialData[name] !== "") {
      setErrors({
        ...errors,
        [name]: "This field cannot be emptied once it has a value.",
      });
    } else {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleRefresh = (key: string) => {
    setFormData({ ...formData, [key]: initialData[key] });
    setErrors({ ...errors, [key]: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dataToSubmit = { ...formData };
      Object.keys(dataToSubmit).forEach((key) => {
        if (dataToSubmit[key] === "") {
          delete dataToSubmit[key];
        }
      });

      console.log("Data being sent:", dataToSubmit);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/health-data`,
        dataToSubmit
      );

      setRiskScore(response.data.riskScore);
      setMissingParameters(response.data.missingParams || []);

      alert("Health data updated successfully!");
      onDataSubmitted();
    } catch (error) {
      console.error("Error submitting health data:", error);
      alert("Error submitting health data. Please try again.");
    }
  };

  const isFormValid = () => {
    return Object.keys(errors).every((key) => !errors[key]);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {Object.entries(fieldNames).map(([key, label]) => (
        <Box key={key} sx={{ mb: 2, position: "relative" }}>
          <TextField
            name={key}
            label={label}
            value={formData[key] || ""}
            onChange={handleChange}
            type="number"
            fullWidth
            error={!!errors[key]}
            helperText={errors[key]}
          />
          {errors[key] && (
            <IconButton
              size="small"
              onClick={() => handleRefresh(key)}
              sx={{
                position: "absolute",
                right: -30,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!isFormValid()}
      >
        Update Health Data
      </Button>
      <Button
        onClick={onClose}
        variant="outlined"
        color="secondary"
        sx={{ ml: 2 }}
      >
        Cancel
      </Button>

      {riskScore !== null ? (
        <Box mt={2}>
          <Typography variant="h6">Risk Score: {riskScore}</Typography>
        </Box>
      ) : (
        missingParameters.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle1">
              Unable to calculate risk score. Missing information:
            </Typography>
            <ul>
              {missingParameters.map((param, index) => (
                <li key={index}>{fieldNames[param] || param}</li>
              ))}
            </ul>
          </Alert>
        )
      )}
    </Box>
  );
};

export default HealthDataForm;
